import {
  cardMutedTextClass,
  cardSurfaceClass,
  cardTitleTextClass,
  ghostBtnClass,
  inputClass,
  mfgAccentBtnClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type {
  MfgBomAuditNode,
  MfgBomAuditNodeResult,
  MfgBomAuditSession,
} from "@/lib/manufacturing/types/bomAudit";
import {
  isBomAuditNodeDrillable,
  MFG_BOM_AUDIT_KIND_LABELS,
  MFG_BOM_AUDIT_RESULT_LABELS,
} from "@/lib/manufacturing/types/bomAudit";

const RESULTS: MfgBomAuditNodeResult[] = ["ok", "issue", "skipped", "unreviewed"];

type Props = {
  session: MfgBomAuditSession;
  nodes: MfgBomAuditNode[];
  stackDepth: number;
  sessionNotes: string;
  busy: boolean;
  onSessionNotes: (v: string) => void;
  onSaveNode: (nodeId: string, patch: { result?: MfgBomAuditNodeResult; notes?: string }) => void;
  onStartSubAudit: (nodeId: string) => void;
  onOpenSubAudit: (sessionId: string) => void;
  onComplete: () => void;
  onLeavePartial: () => void;
  onAbandon: () => void;
  onGoUp: () => void;
};

const labelClass = `flex flex-col gap-1 text-xs text-gray-700 dark:text-zinc-300`;

export function ManufacturingBomAuditWalk(props: Props) {
  const unreviewed = props.nodes.filter((n) => n.result === "unreviewed").length;

  return (
    <div className="flex flex-col gap-3">
      <div
        className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 ${cardSurfaceClass}`}
      >
        <div className="min-w-0">
          <div className={`text-sm font-medium ${cardTitleTextClass}`}>
            {MFG_BOM_AUDIT_KIND_LABELS[props.session.audit_kind]} · {props.session.status}
          </div>
          <div className={`text-xs ${cardMutedTextClass}`}>
            {unreviewed} unreviewed · stack depth {props.stackDepth}
            {props.session.parent_session_id ? " · sub-audit" : ""}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {props.stackDepth > 1 ? (
            <button
              type="button"
              className={ghostBtnClass}
              disabled={props.busy}
              onClick={props.onGoUp}
            >
              Up to parent
            </button>
          ) : null}
          <button
            type="button"
            className={ghostBtnClass}
            disabled={props.busy}
            onClick={props.onLeavePartial}
          >
            Leave partial
          </button>
          <button
            type="button"
            className={mfgSecondaryBtnClass}
            disabled={props.busy}
            onClick={props.onAbandon}
          >
            Abandon
          </button>
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            disabled={props.busy || unreviewed > 0}
            onClick={props.onComplete}
            title={unreviewed > 0 ? "Review all nodes first" : "Complete this audit"}
          >
            Complete
          </button>
        </div>
      </div>

      <label className={labelClass}>
        Session notes
        <textarea
          className={inputClass}
          rows={2}
          value={props.sessionNotes}
          disabled={props.busy}
          onChange={(e) => props.onSessionNotes(e.target.value)}
        />
      </label>

      <ul className="flex flex-col gap-2">
        {props.nodes.map((node) => (
          <BomAuditNodeRow
            key={node.id}
            node={node}
            busy={props.busy}
            onSave={props.onSaveNode}
            onStartSubAudit={props.onStartSubAudit}
            onOpenSubAudit={props.onOpenSubAudit}
          />
        ))}
        {props.nodes.length === 0 ? (
          <li
            className={`rounded-lg border border-dashed border-gray-200 px-3 py-6 text-center text-sm dark:border-zinc-700 ${cardMutedTextClass}`}
          >
            No BOM lines on this recipe.
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function BomAuditNodeRow(props: {
  node: MfgBomAuditNode;
  busy: boolean;
  onSave: (nodeId: string, patch: { result?: MfgBomAuditNodeResult; notes?: string }) => void;
  onStartSubAudit: (nodeId: string) => void;
  onOpenSubAudit: (sessionId: string) => void;
}) {
  const drillable = isBomAuditNodeDrillable(props.node);
  const hasChild = Boolean(props.node.sub_audit_session_id);

  return (
    <li className={`p-3 ${cardSurfaceClass}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className={`text-sm ${cardTitleTextClass}`}>{props.node.display_label}</div>
          <div className={`text-xs ${cardMutedTextClass}`}>
            {props.node.node_kind}
            {props.node.quantity != null ? ` · qty ${props.node.quantity}` : ""}
            {" · "}
            {MFG_BOM_AUDIT_RESULT_LABELS[props.node.result]}
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {RESULTS.map((r) => (
            <button
              key={r}
              type="button"
              className={
                props.node.result === r ? mfgAccentBtnClass : ghostBtnClass
              }
              disabled={props.busy}
              onClick={() => props.onSave(props.node.id, { result: r })}
            >
              {MFG_BOM_AUDIT_RESULT_LABELS[r]}
            </button>
          ))}
        </div>
      </div>
      <label className={`mt-2 ${labelClass}`}>
        Node notes
        <input
          className={inputClass}
          defaultValue={props.node.notes}
          disabled={props.busy}
          onBlur={(e) => {
            const next = e.target.value;
            if (next !== props.node.notes) {
              props.onSave(props.node.id, { notes: next });
            }
          }}
        />
      </label>
      <div className="mt-2 flex gap-2">
        {drillable ? (
          <button
            type="button"
            className={mfgSecondaryBtnClass}
            disabled={props.busy}
            onClick={() => props.onStartSubAudit(props.node.id)}
          >
            Drill in
          </button>
        ) : null}
        {hasChild ? (
          <button
            type="button"
            className={ghostBtnClass}
            disabled={props.busy}
            onClick={() => props.onOpenSubAudit(props.node.sub_audit_session_id!)}
          >
            Open sub-audit
          </button>
        ) : null}
      </div>
    </li>
  );
}
